import React, { useEffect, useState, useCallback } from 'react';
import i18n from 'i18n-js';
import moment from 'moment';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import { withRouter } from 'react-router-dom';
import { DateRangePicker } from 'components/UI';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import Container from '@material-ui/core/Container';
import { forceTimezone, toQueryString } from 'utils';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentsActions } from 'state/modules/documents';
import VersionModal from 'components/Documents/VersionModal';
import InputAdornment from '@material-ui/core/InputAdornment';
import { transformAsyncErrors, removeEmpty } from 'utils/utils';
import DocumentVersionsTable from 'components/Documents/DocumentVersionsTable';

const useStyles = makeStyles((theme) => ({
  container: {
    minWidth: '100%',
    margin: 0,
  },
  searchIcon: {
    '& svg': {
      color: theme.palette.secondary.light,
      fontSize: theme.typography.pxToRem(14),
    },
  },
  actionBtn: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
    textTransform: 'none',
  },
  subtitle: {
    color: '#4D4F5C',
    fontSize: 13,
  },
}));

const DocumentsVersionsPage = ({ history }) => {
  const classes = useStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const { documentId } = useParams();
  const [modalMode, setModalMode] = useState('idle');
  const [asyncErrors, setAsyncErrors] = useState({});
  const [currentVersion, setCurrentVersion] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const {
    fetchingVersions,
    versions: { list, pagination },
  } = useSelector((state) => state.documents);

  const getVersions = useCallback(() => {
    dispatch(DocumentsActions.fetchVersionsByDocumentId(documentId, params));
  }, [documentId, dispatch, params]);

  const [toggleDeleteVersion, DeleteVersionConfirm] = useConfirm({
    open: false,
    title: i18n.t('documents.delete_version'),
    message: i18n.t('documents.delete_version_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: async () => {
      dispatch(
        DocumentsActions.deleteVersion(documentId, currentVersion, () => {
          getVersions();
        })
      );
    },
  });

  const onUpdate = async (data) => {
    const response = dispatch(
      DocumentsActions.updateVersion(documentId, currentVersion?.id, data, () => {
        if (modalMode !== 'idle') {
          setModalMode('idle');
          dispatch(DocumentsActions.fetchVersionsByDocumentId(documentId, params));
        }
      })
    );
    if (response.ok && modalMode !== 'idle') {
      setModalMode('idle');
    } else {
      setAsyncErrors({
        ...asyncErrors,
        updateVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  const handleMarkDefault = async (row) => {
    const payload = {
      default: true,
      version: row.version,
    };
    dispatch(DocumentsActions.updateVersion(documentId, row?.id, payload, getVersions, true));
  };

  useEffect(() => {
    dispatch(DocumentsActions.fetchVersionsByDocumentId(documentId, params));
  }, [params, documentId, dispatch]);

  const handleDelete = (id) => {
    setCurrentVersion(id);
    toggleDeleteVersion();
  };

  const closeVersionModal = () => {
    setModalMode('idle');
    setAsyncErrors({});
  };

  const handleEdit = (row) => {
    setCurrentVersion(row);
    setModalMode('update');
  };

  const handleParams = (params) => {
    const data = { ...params };
    setParams({ ...data });
    const _params = toQueryString(data);
    history.replace(buildUrl(`/documents/${documentId}/versions${_params}`));
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/documents/${documentId}/versions/${row?.id}`));
  };

  /**
   * Search filter
   * @param {*} name Name of the field
   * @param {*} value Value of the field
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    handleParams(filters);
  };

  /**
   * Handle date range picker onChange event
   */
  const onChange = (dates, field = '') => {
    let { startDate, endDate } = dates;
    startDate = startDate
      ? forceTimezone(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:00Z')
      : undefined;
    endDate = endDate
      ? forceTimezone(endDate).format('YYYY-MM-DDTHH:mm:00Z')
      : field && startDate
      ? moment(startDate).format('YYYY-MM-DDTHH:mm:00Z')
      : undefined;

    const newParams = {
      ...params,
      [`${field}end_date`]: endDate,
      [`${field}start_date`]: startDate,
    };

    if (!newParams[`${field}end_date`]) {
      delete newParams[`${field}end_date`];
    }

    if (!newParams[`${field}start_date`]) {
      delete newParams[`${field}start_date`];
    }

    handleParams(newParams);
  };

  const onCreate = async (data) => {
    const response = await dispatch(DocumentsActions.createVersion(documentId, data));
    if (response.ok) {
      setModalMode('idle');
      history.push(buildUrl(`/documents/${documentId}/versions/${response.data.id}`));
    } else {
      setAsyncErrors({
        ...asyncErrors,
        createVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  return (
    <Layout title={i18n.t('documents.versions')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item sm={8}>
                <Title title={i18n.t('documents.versions')} />
              </Grid>
              <Grid item>
                <Box display="flex" flexWrap="wrap">
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="create-document"
                    onClick={() => setModalMode('create')}
                    className={classes.actionBtn}
                  >
                    {i18n.t('documents.create_new_version')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Box pb={3}>
              <Typography variant="subtitle2" className={classes.subtitle}>
                {i18n.t('common.filter_by')}
              </Typography>
            </Box>
            <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  margin="none"
                  label={i18n.t('documents.search')}
                  field={{ onChange: search, name: 'keyword', value: params.keyword }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item>
                <DateRangePicker
                  showClearDates
                  onDatesChange={(dates) => onChange(dates, 'expiration_')}
                  endDate={
                    params.expiration_end_date ? moment(params.expiration_end_date) : undefined
                  }
                  startDate={
                    params.expiration_start_date ? moment(params.expiration_start_date) : undefined
                  }
                  label={i18n.t('documents.expiration_date')}
                />
              </Grid>
              <Grid item>
                <DateRangePicker
                  showClearDates
                  onDatesChange={(dates) => onChange(dates)}
                  label={i18n.t('documents.date_range')}
                  endDate={params.end_date ? moment(params.end_date) : undefined}
                  startDate={params.start_date ? moment(params.start_date) : undefined}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <DocumentVersionsTable
              versions={list}
              page={params.page}
              limit={params.limit}
              handleEdit={handleEdit}
              pagination={pagination}
              handleSort={handleSort}
              loading={fetchingVersions}
              handleDelete={handleDelete}
              handleRowClick={handleRowClick}
              handlePageChange={handleChangePage}
              defaultSortAsc={!params?.sort_desc}
              handleMarkDefault={handleMarkDefault}
              defaultSortField={params?.sort_field}
              handlePerRowsChange={handlePerRowsChange}
            />
          </Grid>
        </Grid>
        <DeleteVersionConfirm />
        {modalMode !== 'idle' && (
          <VersionModal
            mode={modalMode}
            open={modalMode ? true : false}
            handleCreate={modalMode === 'update' ? onUpdate : onCreate}
            handleClose={closeVersionModal}
            asyncErrors={
              asyncErrors === 'update' ? asyncErrors.updateVersion : asyncErrors.createVersion
            }
            {...(modalMode === 'update' && removeEmpty(currentVersion))}
          />
        )}
      </Container>
    </Layout>
  );
};

export default withRouter(DocumentsVersionsPage);
